import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Email } from '@/types/email';
import type { AiAnnotation, AiSuggestedTask, AiTaskSuggestionResult } from '@/types/ai';
import { fetchMessageTaskSuggestions, runMessageTaskSuggestions } from '@/api/aiTasksApi';
import { ApiError } from '@/api/core/client';

export interface TaskSuggestionEntry extends AiSuggestedTask {
  id: string;
  title: string;
  dueDate: string | null;
  dueText: string | null;
  bullets: string[];
  notes: string;
}

interface UseMessageTaskSuggestionsResult {
  annotation: AiAnnotation | null;
  suggestions: TaskSuggestionEntry[];
  isLoading: boolean;
  error: string | null;
  hasFetched: boolean;
  requestSuggestions: (options?: { force?: boolean }) => Promise<AiAnnotation | null>;
  resetError: () => void;
}

function coerceTasks(annotation: AiAnnotation | null): TaskSuggestionEntry[] {
  if (!annotation?.result) {
    return [];
  }
  const result = annotation.result as unknown;
  if (!result || typeof result !== 'object') {
    return [];
  }
  const suggestionResult = result as AiTaskSuggestionResult;
  if (!('tasks' in suggestionResult) || !Array.isArray(suggestionResult.tasks)) {
    return [];
  }
  const tasks = suggestionResult.tasks;
  return tasks
    .map<TaskSuggestionEntry | null>((task, index) => {
      if (!task || typeof task !== 'object') {
        return null;
      }
      const title = typeof task.title === 'string' ? task.title.trim() : '';
      if (!title) {
        return null;
      }
      const bullets = Array.isArray(task.bullets)
        ? task.bullets
            .map((bullet) => (typeof bullet === 'string' ? bullet.trim() : null))
            .filter((bullet): bullet is string => bullet !== null)
        : [];
      const notes =
        typeof task.notes === 'string' && task.notes.trim()
          ? task.notes.trim()
          : bullets.length
            ? ['Key details:', ...bullets.map((bullet) => `- ${bullet}`)].join('\n')
            : '';
      const dueDate = task.dueDate ?? null;
      const dueText = task.dueText ?? null;
      return {
        id: task.id || `ai-task-${index}`,
        title,
        dueDate: dueDate || null,
        dueText: dueText || null,
        bullets,
        notes,
        confidence: task.confidence ?? null,
      };
    })
    .filter((entry): entry is TaskSuggestionEntry => Boolean(entry));
}

export function useMessageTaskSuggestions(email: Email | null): UseMessageTaskSuggestionsResult {
  const [annotation, setAnnotation] = useState<AiAnnotation | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const emailId = email?.id ?? null;
  const accountId = email?.accountId ?? null;

  // Identity anchor for the EXPLICIT request path (X3): task suggestions
  // requested for message A must never render under message B after navigation
  // (LLM latency makes this common); the persistent EmailDetail keeps this hook
  // mounted across message changes.
  const currentEmailIdRef = useRef<string | null>(emailId);

  useEffect(() => {
    currentEmailIdRef.current = emailId;
    setAnnotation(null);
    setError(null);
    setHasFetched(false);
  }, [accountId, emailId]);

  useEffect(() => {
    if (!emailId || !accountId || hasFetched) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchMessageTaskSuggestions({ accountId, messageId: emailId })
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result) {
          setAnnotation(result);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        if (err instanceof ApiError && (err.status === 404 || err.status === 409)) {
          return;
        }
        const message = err instanceof Error ? err.message : 'Failed to load suggestions';
        setError(message);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setHasFetched(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accountId, emailId, hasFetched]);

  const requestSuggestions = useCallback(
    async (options?: { force?: boolean }) => {
      if (!accountId || !emailId) {
        return null;
      }
      const { force = false } = options || {};
      const requestEmailId = emailId;
      setError(null);
      setLoading(true);
      try {
        const result = await runMessageTaskSuggestions({
          accountId,
          messageId: emailId,
          force,
          source: 'detail_view',
        });
        // Drop the response if the user navigated away mid-request (X3).
        if (currentEmailIdRef.current !== requestEmailId) {
          return null;
        }
        setAnnotation(result);
        setHasFetched(true);
        return result;
      } catch (err) {
        if (currentEmailIdRef.current !== requestEmailId) {
          return null;
        }
        if (err instanceof ApiError && err.status === 503) {
          setError('AI suggestions are temporarily unavailable');
        } else {
          setError(err instanceof Error ? err.message : 'Failed to run suggestions');
        }
        throw err;
      } finally {
        if (currentEmailIdRef.current === requestEmailId) {
          setLoading(false);
        }
      }
    },
    [accountId, emailId]
  );

  const suggestions = useMemo(() => coerceTasks(annotation), [annotation]);

  const resetError = useCallback(() => setError(null), []);

  return {
    annotation,
    suggestions,
    isLoading,
    error,
    hasFetched,
    requestSuggestions,
    resetError,
  };
}
